// Field-name bridges between the API's event payloads (events.ts) and the web client's (web/src/lib/api-types.ts).
// No zod here, so clients can import it without pulling the schemas into their bundle.
import type { ApprovalOutcome, ApprovalTier, ArtifactKind } from './events.ts';

/** Approval tiers as the numeric risk level the web client shows (0 read .. 3 sensitive). */
export const APPROVAL_TIER_RANK: Record<ApprovalTier, 0 | 1 | 2 | 3> = { read: 0, change: 1, consequential: 2, sensitive: 3 };
export const APPROVAL_TIER_BY_RANK: readonly ApprovalTier[] = ['read', 'change', 'consequential', 'sensitive'];

const ARTIFACT_KINDS: readonly string[] = ['document', 'table', 'slides', 'code', 'html', 'image', 'chart', 'other'];
const OUTCOMES: readonly string[] = ['allow', 'allow_task', 'deny', 'timeout', 'cancelled'];

/** Finer artifact types (BUILD-01) carried in `subtype`, mapped onto the six kinds. */
export const ARTIFACT_SUBTYPE_KIND: Record<string, ArtifactKind> = {
  svg: 'image', react: 'code', markdown: 'document', doc: 'document', sheet: 'table', csv: 'table', mermaid: 'chart', pdf: 'document', site: 'html', app: 'code',
};

type Loose = Record<string, unknown>;
const isObj = (v: unknown): v is Loose => typeof v === 'object' && v !== null && !Array.isArray(v);
const has = (o: Loose, k: string) => o[k] !== undefined && o[k] !== null;

/** Client decision words (web ApprovalCard) to the API's outcomes. */
export const CLIENT_DECISION_TO_OUTCOME: Record<string, ApprovalOutcome> = {
  approved: 'allow', approve: 'allow', edited: 'allow', allow: 'allow', allow_task: 'allow_task',
  denied: 'deny', deny: 'deny', handoff: 'deny', expired: 'timeout', timeout: 'timeout', cancelled: 'cancelled',
};
/** API outcomes to the client's words. */
export const OUTCOME_TO_CLIENT_DECISION: Record<ApprovalOutcome, 'approved' | 'denied' | 'expired'> = {
  allow: 'approved', allow_task: 'approved', deny: 'denied', timeout: 'expired', cancelled: 'expired',
};

function domainOf(url: unknown): string | undefined {
  if (typeof url !== 'string' || !url) return undefined;
  try {
    return new URL(url).hostname.replace(/^www\./, '');
  } catch {
    return undefined;
  }
}

/**
 * Fills the API's field names from the client's ones (for handlers that emit web-shaped payloads), leaving
 * everything already in API shape untouched. Runs before validation in the job emitter. Pure; returns a new object.
 */
export function normalizeEventData(type: string, data: unknown): unknown {
  if (!isObj(data)) return data;
  const d: Loose = { ...data };
  switch (type) {
    case 'text.done':
      if (!has(d, 'text')) d.text = '';
      break;
    case 'step.started':
      if (!has(d, 'title') && has(d, 'label')) d.title = d.label;
      break;
    case 'tool.call.started':
    case 'tool.call.args':
    case 'tool.call.result':
      if (!has(d, 'callId') && has(d, 'toolCallId')) d.callId = d.toolCallId;
      if (type === 'tool.call.result' && d.isError === true && !has(d, 'error') && typeof d.result === 'string') d.error = d.result;
      break;
    case 'citation.added': {
      const c = isObj(d.citation) ? d.citation : null;
      if (c) {
        if (!has(d, 'id')) d.id = c.id;
        if (!has(d, 'index')) d.index = typeof c.n === 'number' && c.n > 0 ? c.n : 1;
        if (!has(d, 'url')) d.url = typeof c.url === 'string' ? c.url : '';
        if (!has(d, 'title')) d.title = c.title;
        if (!has(d, 'snippet') && typeof c.excerpt === 'string') d.snippet = c.excerpt;
        for (const k of ['cited', 'domain', 'date', 'relevance', 'lowQuality']) if (!has(d, k) && has(c, k)) d[k] = c[k];
        if (!has(d, 'sourceType') && typeof c.type === 'string') d.sourceType = c.type;
      }
      break;
    }
    case 'artifact.created': {
      const a = isObj(d.artifact) ? d.artifact : null;
      if (a) {
        if (!has(d, 'artifactId')) d.artifactId = a.id;
        if (!has(d, 'title')) d.title = a.title;
        if (!has(d, 'kind')) d.kind = a.kind;
        if (!has(d, 'content') && typeof a.content === 'string') d.content = a.content;
        if (!has(d, 'mime') && typeof a.mime === 'string') d.mime = a.mime;
        if (!has(d, 'version') && typeof a.version === 'number') d.version = a.version;
      }
      const kind = String(d.kind ?? '');
      if (kind && !ARTIFACT_KINDS.includes(kind)) {
        if (!has(d, 'subtype')) d.subtype = kind;
        d.kind = ARTIFACT_SUBTYPE_KIND[kind] ?? 'other';
      }
      break;
    }
    case 'approval.requested': {
      const a = isObj(d.approval) ? d.approval : null;
      if (a) {
        if (!has(d, 'approvalId')) d.approvalId = a.id;
        if (!has(d, 'summary')) d.summary = a.summary;
        if (!has(d, 'tier')) d.tier = typeof a.tier === 'number' ? APPROVAL_TIER_BY_RANK[a.tier] : a.tier;
        if (!has(d, 'expiresAt')) d.expiresAt = typeof a.expiresAt === 'string' ? a.expiresAt : new Date(Date.now() + 15 * 60_000).toISOString();
        for (const k of ['primaryLabel', 'preview', 'stepId', 'allowTask']) if (!has(d, k) && has(a, k)) d[k] = a[k];
      }
      if (typeof d.tier === 'number') d.tier = APPROVAL_TIER_BY_RANK[d.tier] ?? d.tier;
      break;
    }
    case 'approval.resolved':
      if (typeof d.decision === 'string' && CLIENT_DECISION_TO_OUTCOME[d.decision] && !OUTCOMES.includes(d.decision)) {
        d.decision = CLIENT_DECISION_TO_OUTCOME[d.decision];
      }
      break;
    case 'usage.updated':
      if (!has(d, 'tokensIn') && typeof d.inputTokens === 'number') d.tokensIn = d.inputTokens;
      if (!has(d, 'tokensOut') && typeof d.outputTokens === 'number') d.tokensOut = d.outputTokens;
      if (!has(d, 'costCredits') && typeof d.credits === 'number') d.costCredits = d.credits;
      if (!has(d, 'tokensIn')) d.tokensIn = 0;
      if (!has(d, 'tokensOut')) d.tokensOut = 0;
      if (!has(d, 'costCredits')) d.costCredits = 0;
      if (!has(d, 'provider')) d.provider = 'unknown';
      if (!has(d, 'model')) d.model = 'unknown';
      break;
    case 'run.finished':
      if (d.status === 'completed') d.status = 'succeeded';
      break;
    default:
      break;
  }
  return d;
}

/**
 * Adds the client's field names to an API payload (web `api-types.ts`): step `label`, `toolCallId`, `isError`, nested
 * `citation` / `artifact` / `approval` (numeric tier), approval decisions as approved/denied/expired, usage
 * `credits`/`inputTokens`/`outputTokens`, run.finished `completed`. API fields stay, so readers of either shape work.
 */
export function toClientEventData(type: string, data: unknown): unknown {
  if (!isObj(data)) return data;
  const d: Loose = { ...data };
  switch (type) {
    case 'step.started':
      if (!has(d, 'label') && has(d, 'title')) d.label = d.title;
      break;
    case 'tool.call.started':
    case 'tool.call.args':
    case 'tool.call.result':
      if (!has(d, 'toolCallId') && has(d, 'callId')) d.toolCallId = d.callId;
      if (type === 'tool.call.result' && !has(d, 'isError') && has(d, 'error')) d.isError = true;
      break;
    case 'citation.added':
      if (!isObj(d.citation) && has(d, 'id')) {
        d.citation = {
          id: d.id, n: d.cited === false ? undefined : d.index, url: d.url || undefined, title: d.title, excerpt: d.snippet,
          domain: d.domain ?? domainOf(d.url), date: d.date, type: d.sourceType, relevance: d.relevance, cited: d.cited ?? true, lowQuality: d.lowQuality,
        };
      }
      break;
    case 'artifact.created':
      if (!isObj(d.artifact) && has(d, 'artifactId')) {
        d.artifact = { id: d.artifactId, kind: d.subtype ?? d.kind, title: d.title, version: d.version ?? 1, content: d.content, mime: d.mime };
      }
      break;
    case 'approval.requested':
      if (!isObj(d.approval) && has(d, 'approvalId')) {
        const tier = APPROVAL_TIER_RANK[d.tier as ApprovalTier] ?? 2;
        d.approval = {
          id: d.approvalId, tier, summary: d.summary,
          preview: d.preview ?? (typeof d.details === 'string' ? d.details : undefined),
          primaryLabel: d.primaryLabel, stepId: d.stepId, expiresAt: d.expiresAt,
          allowTask: d.allowTask ?? tier <= 1,
        };
      }
      break;
    case 'approval.resolved':
      if (typeof d.decision === 'string' && d.decision in OUTCOME_TO_CLIENT_DECISION) {
        d.outcome = d.decision;
        d.decision = OUTCOME_TO_CLIENT_DECISION[d.decision as ApprovalOutcome];
      }
      break;
    case 'usage.updated':
      if (!has(d, 'credits') && typeof d.costCredits === 'number') d.credits = d.costCredits;
      if (!has(d, 'inputTokens') && typeof d.tokensIn === 'number') d.inputTokens = d.tokensIn;
      if (!has(d, 'outputTokens') && typeof d.tokensOut === 'number') d.outputTokens = d.tokensOut;
      break;
    case 'run.finished':
      if (d.status === 'succeeded') d.status = 'completed';
      break;
    default:
      break;
  }
  return d;
}

/** A whole event envelope with client field names (see toClientEventData). */
export function toClientEvent<E extends { type: string; data: unknown }>(ev: E): E {
  return { ...ev, data: toClientEventData(ev.type, ev.data) };
}

